<script setup lang="ts">
import { computed, type ComputedRef } from "vue";

import MenuLink from "@/common/components/menu/MenuLink.vue";
import DropdownVue from "@/common/components/menu/NavigationDropdown.vue";
import { useScreenSize } from "@/common/composables/useScreenSize";
import type { NavigationLink } from "@/common/types";
import PlatformPicker from "@/platforms/components/PlatformPicker.vue";
import type { Permission } from "@/roles/types";
import { useCurrentUser } from "@/users/public/composables";

import type { BaseIconProps } from "../icons/BaseIcon.vue";

const { userHasPermission } = useCurrentUser();

const globalNavigationLinks: NavigationLink[] = [
  {
    link: "/",
    name: "Home",
    icon: "heroicons:home-solid",
    permissionsNeeded: ["Admin.Access"] as Permission[],
  },
  {
    link: "/platforms",
    name: "Platforms",
    icon: "heroicons:square-3-stack-3d-solid",
    permissionsNeeded: ["Admin.Access"] as Permission[],
  },
  {
    link: "/app-versions",
    name: "App Versions",
    icon: "heroicons:device-phone-mobile-solid",
    permissionsNeeded: ["Developer.Access"] as Permission[],
  },
];

const monitor = useScreenSize();

const timeFrame = computed(() => (monitor.isMobile.value ? "day" : "week"));

const mwsNavigationLinks = computed<NavigationLink[]>(() => [
  {
    link: "/customers",
    name: "Customers",
    icon: "heroicons:users-solid",
    permissionsNeeded: ["Admin.Access"] as Permission[],
  },
  {
    link: "/beneficiaries",
    name: "Beneficiaries",
    icon: "heroicons:heart-solid",
    permissionsNeeded: ["Admin.Access"] as Permission[],
  },
  {
    link: "/orders",
    name: "Orders",
    icon: "heroicons:credit-card-solid",
    permissionsNeeded: ["Admin.Access"] as Permission[],
  },
  {
    link: "/roles",
    name: "Role Management",
    icon: "heroicons:shield-check-solid",
    permissionsNeeded: ["Developer.Access"] as Permission[],
  },
  {
    link: "/categories",
    name: "Categories",
    icon: "heroicons:swatch-solid",
    permissionsNeeded: ["Categories.Read"] as Permission[],
  },
  {
    link: "/admin-tools",
    name: "Admin Tools",
    icon: "heroicons:adjustments-horizontal-solid",
    permissionsNeeded: ["Admin.Access"] as Permission[],
  },
  {
    link: "/pick-pack-ship/regular",
    name: "Pick, Pack & Ship",
    icon: "heroicons:paper-airplane-solid",
    permissionsNeeded: ["Admin.Access"] as Permission[],
  },
  {
    link: "/upsell/discounts",
    name: "Upsell Discounts",
    icon: "heroicons:receipt-percent-solid",
    permissionsNeeded: ["Upsell.Access"] as Permission[],
  },
]);

const framingNavigationLinks: NavigationLink[] = [
  {
    link: "/framing/passe-partouts",
    name: "Passe-partouts",
    icon: "heroicons:photo-solid",
    permissionsNeeded: ["FramingAdmin.Access"] as Permission[],
  },
  {
    link: "/framing/framing-assets",
    name: "Framing Assets",
    icon: "heroicons:photo-solid",
    permissionsNeeded: ["MockupSets.Read"] as Permission[],
  },
];

const fabricksNavigationLinks: NavigationLink[] = [
  {
    link: "/fabricks/trackable-products",
    name: "Trackable Products",
    icon: "heroicons:shopping-cart-solid",
    permissionsNeeded: ["FabricksAdmin.Access"] as Permission[],
  },
  {
    link: "/fabricks/batches",
    name: "Batches",
    icon: "heroicons:square-3-stack-3d",
    permissionsNeeded: ["FabricksAdmin.Access"] as Permission[],
  },
  {
    link: "/fabricks/partners",
    name: "Partners",
    icon: "heroicons:user-group-solid",
    permissionsNeeded: ["FabricksAdmin.Access"] as Permission[],
  },
  {
    link: "/fabricks/chips",
    name: "Chips",
    icon: "heroicons:cpu-chip-solid",
    permissionsNeeded: ["FabricksAdmin.Access"] as Permission[],
  },
];

const filteredGlobalNavigationLinks = computed(() => {
  return globalNavigationLinks.filter((navigationLink) =>
    userHasPermission(navigationLink.permissionsNeeded),
  );
});

const filteredMwsNavigationLinks = computed(() => {
  return mwsNavigationLinks.value.filter((navigationLink) =>
    userHasPermission(navigationLink.permissionsNeeded),
  );
});

const filteredFramingNavigationLinks = computed(() => {
  return framingNavigationLinks.filter((navigationLink) =>
    userHasPermission(navigationLink.permissionsNeeded),
  );
});

const filteredFabricksNavigationLinks = computed(() => {
  return fabricksNavigationLinks.filter((navigationLink) =>
    userHasPermission(navigationLink.permissionsNeeded),
  );
});

type NavigationSubMenuItem = {
  name: string;
  permissionsNeeded?: Permission[];
  icon: BaseIconProps["name"];
  subItems: {
    link: string;
    name: string;
    permissionsNeeded?: Permission[];
  }[];
};

const filterVisibleSubMenuItems = (menuItems: NavigationSubMenuItem[]) =>
  menuItems
    .map((menuItem) => {
      const filteredSubItems = menuItem.subItems.filter((subItem) => {
        if ("permissionsNeeded" in subItem && subItem.permissionsNeeded) {
          return userHasPermission(subItem.permissionsNeeded);
        }

        return true;
      });

      return {
        ...menuItem,
        subItems: filteredSubItems,
      };
    })
    .filter((menuItem) => {
      const hasMenuPermission =
        !menuItem.permissionsNeeded ||
        userHasPermission(menuItem.permissionsNeeded);

      const hasVisibleSubItems = menuItem.subItems.length > 0;

      return hasMenuPermission && hasVisibleSubItems;
    });

const platformSubMenuItems: NavigationSubMenuItem[] = [
  {
    name: "Outbox",
    icon: "heroicons:archive-box",
    subItems: [
      {
        link: "/outbox/catalog",
        name: "Catalog",
        permissionsNeeded: ["Developer.Access"] as Permission[],
      },
      {
        link: "/outbox/finance",
        name: "Finance",
        permissionsNeeded: ["Developer.Access"] as Permission[],
      },
      {
        link: "/outbox/sports",
        name: "Sports",
        permissionsNeeded: ["Developer.Access"] as Permission[],
      },
      {
        link: "/outbox/identity",
        name: "Identity",
        permissionsNeeded: ["Developer.Access"] as Permission[],
      },
    ],
  },
];

const filteredPlatformSubMenuItems = computed(() =>
  filterVisibleSubMenuItems(platformSubMenuItems),
);

const subMenuItems: ComputedRef<NavigationSubMenuItem[]> = computed(() => [
  {
    name: "Events",
    icon: "heroicons:shopping-bag-solid",
    subItems: [
      {
        link: `/events/calendar/${timeFrame.value}?statuses=["published","draft","confirmed"]`,
        name: "Event Calendar",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/events/list",
        name: "Event List",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/events/inbounds",
        name: "Inbounds",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/events/predict",
        name: "Event Yield Prediction",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
    ],
  },
  {
    name: "Product management",
    icon: "heroicons:shopping-bag-solid",
    subItems: [
      {
        link: "/product-listings",
        name: "Product Listings",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/products/list",
        name: "Products",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/product-types",
        name: "Types",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/labels",
        name: "Labels",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
    ],
  },
  {
    name: "Resale",
    icon: "heroicons:arrows-right-left-solid",
    subItems: [
      {
        link: "/resale/buyer-orders",
        name: "Buyer orders",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/resale/seller-orders",
        name: "Seller orders",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
    ],
  },
  {
    name: "Finance",
    icon: "heroicons:currency-dollar-solid",
    subItems: [
      {
        link: "/partners",
        name: "Partners",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/partner-performance",
        name: "Partner Performance",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/contract-payouts",
        name: "Contract Payouts",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/vendors",
        name: "Vendors",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/legal-organisations",
        name: "Legal Organisations",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/adyen-merchants",
        name: "Adyen Merchants",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/event-settings/list",
        name: "Event Settings",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
    ],
  },
  {
    name: "Shipping",
    icon: "heroicons:truck-solid",
    subItems: [
      {
        link: "/global-shipping-configs",
        name: "Global Shipping Configs",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/parcel-types",
        name: "Parcel Types",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/fabricks/chips/scan",
        name: "Find Order (NFC)",
        permissionsNeeded: ["FabricksAdmin.Access"] as Permission[],
      },
    ],
  },
  {
    name: "Sports management",
    icon: "heroicons:trophy-solid",
    subItems: [
      {
        link: "/sports/list",
        name: "Sports",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/teams/list",
        name: "Teams",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: userHasPermission(["Admin.Access"])
          ? "/kits/management/pending"
          : "/kits/management",
        name: "Mockup Management",
        permissionsNeeded: ["Kits.Read"] as Permission[],
      },
      {
        link: "/kits/list",
        name: "Kits",
        permissionsNeeded: ["Kits.Read"] as Permission[],
      },
      {
        link: "/athletes/list",
        name: "Athletes",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/leagues/list",
        name: "Leagues",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/fixtures",
        name: "Fixtures",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
      {
        link: "/sports/search",
        name: "Search",
        permissionsNeeded: ["Admin.Access"] as Permission[],
      },
    ],
  },
]);

const filteredSubMenuItems = computed(() =>
  filterVisibleSubMenuItems(subMenuItems.value),
);

defineEmits<{
  (e: "close-sidebar-click"): void;
}>();
</script>

<template>
  <div>
    <PlatformPicker
      v-if="userHasPermission('GlobalUser.Access')"
      class="mb-4"
    />

    <div
      v-if="filteredGlobalNavigationLinks.length > 0"
      class="sidebar-links__global-section"
    >
      <MenuLink
        v-for="navigationLink in filteredGlobalNavigationLinks"
        :key="navigationLink.name"
        :navigation-link="navigationLink"
      />
      <DropdownVue
        v-for="subMenuItem in filteredPlatformSubMenuItems"
        :key="subMenuItem.name"
        :title="subMenuItem.name"
        :options="subMenuItem.subItems"
        :icon="subMenuItem.icon"
        :data-cy="`vue-navigation-dropdown-${subMenuItem.name
          .toLowerCase()
          .replace(' ', '-')}`"
        @item-click="$emit('close-sidebar-click')"
      />
    </div>

    <div class="sidebar-links__marketplace-section">
      <span class="sidebar-links__nav-section-title">MWS</span>
      <MenuLink
        v-for="navigationLink in filteredMwsNavigationLinks"
        :key="navigationLink.name"
        :navigation-link="navigationLink"
        :data-cy="`vue-navigation-link-${navigationLink.name
          .toLowerCase()
          .replace(' ', '-')}`"
        @click="$emit('close-sidebar-click')"
      />
      <DropdownVue
        v-for="subMenuItem in filteredSubMenuItems"
        :key="subMenuItem.name"
        :title="subMenuItem.name"
        :options="subMenuItem.subItems"
        :icon="subMenuItem.icon"
        :data-cy="`vue-navigation-dropdown-${subMenuItem.name
          .toLowerCase()
          .replace(' ', '-')}`"
        @item-click="$emit('close-sidebar-click')"
      />
    </div>

    <div
      v-if="filteredFramingNavigationLinks.length > 0"
      class="sidebar-links__framing-section"
    >
      <span class="sidebar-links__nav-section-title">Frame the game</span>
      <MenuLink
        v-for="navigationLink in filteredFramingNavigationLinks"
        :key="navigationLink.name"
        :navigation-link="navigationLink"
        @click="$emit('close-sidebar-click')"
      />
    </div>

    <div
      v-if="filteredFabricksNavigationLinks.length > 0"
      class="sidebar-links__fabricks-section"
    >
      <span class="sidebar-links__nav-section-title">Fabricks</span>
      <MenuLink
        v-for="navigationLink in filteredFabricksNavigationLinks"
        :key="navigationLink.name"
        :navigation-link="navigationLink"
        @click="$emit('close-sidebar-click')"
      />
    </div>
  </div>
</template>

<style scoped lang="postcss">
.sidebar-links {
  &__marketplace-section {
    @apply pt-4 space-y-1;
  }

  &__framing-section {
    @apply pt-4 space-y-1;
  }

  &__fabricks-section {
    @apply pt-4 space-y-1;
  }

  &__nav-section-title {
    @apply text-slate-300 pl-2 uppercase text-sm;
  }

  &__link {
    @apply text-gray-100 hover:bg-gray-700 hover:text-white flex items-center py-2 text-sm font-medium rounded-md justify-between;

    &__content {
      @apply flex items-center gap-x-2;

      &__icon {
        @apply text-white text-2xl;
      }
    }
  }
}
</style>
